import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { BodySection, ActionSection } from 'components/UI/Section';
import Section from '../Section';
import { Option, PetType } from '@spike/model';
import { Selectable } from 'model';
import PetTypes from './PetTypes';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import Service from 'model/Service';
import BusinessArea from './BusinessArea/BusinessArea';
import clsx from 'clsx';
import { MobileDivider } from 'components/UI/MobileDivider';
import { MarketplaceBasics } from '@spike/marketplace-model';
import { isStateAllowed, normalizeState } from 'utils/StateUtils';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useOrganizationMarketplacesByServiceCategory from 'hooks/useOrganizationMarketplacesByServiceCategory';
import { useAuth } from 'hooks/useAuth';

interface Props {
    collapsed?: boolean;
    disabled?: boolean;
    editable?: boolean;
    completed?: boolean;
    modifiable?: boolean;
    service: Service;
    optionsBusinessAreas: Array<Option<string>>;
    petTypes: Array<Selectable<PetType>>;
    onEdit?: () => void;
    onChange: (selectedPetType: Array<Selectable<PetType>>) => void;
    onChangeBusinessArea: (businessArea: Option<string>) => void;
    onChangeMarketplaces: (marketplaces: Service['marketplaces']) => void;
    onCollapse: () => void;
    onNext: () => void;
    marketplaceBasics: MarketplaceBasics;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        row: {
            width: '100%',

            [theme.breakpoints.up('md')]: {
                display: 'flex'
            }
        },
        rowDivider: {
            margin: '0px -16px'
        },
        businessAreaContainer: {
            [theme.breakpoints.up('md')]: {
                width: '50%',
                marginTop: '36px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '47px'
            }
        },
        businessAreasNone: {
            display: 'none'
        },
        petTypesContainer: {
            width: '50%'
        },
        locationsContainer: {
            width: '100%',
            [theme.breakpoints.down('lg')]: {
                marginTop: '36px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '47px'
            }
        },
        houseCallContainer: {
            width: '100%',
            [theme.breakpoints.down('lg')]: {
                marginTop: '36px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '47px'
            }
        }
    })
);

export const Category: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const auth = useAuth();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const groomingMarketplaces = useOrganizationMarketplacesByServiceCategory('grooming');

    const existSelected = (options: Array<Selectable<PetType>>) => {
        return options.some(option => option.selected);
    };

    const hasLocationsSelected = props.service.marketplaces.length > 0;
    const disabledNext = !existSelected(props.petTypes) || !hasLocationsSelected;

    const loggedInMarketplace = useMemo(
        () => groomingMarketplaces.find(marketplace => marketplace.id === auth.marketplaceId),
        [groomingMarketplaces, auth.marketplaceId]
    );

    const buildLoggedInMarketplaceEntry = (): Service['marketplaces'] => {
        if (!loggedInMarketplace) {
            return [];
        }

        const existingMarketplace = props.service.marketplaces.find(
            marketplace => marketplace.marketplaceId === loggedInMarketplace.id
        );

        return [
            {
                marketplaceId: loggedInMarketplace.id,
                staffIds: existingMarketplace?.staffIds ?? [],
                taxes: existingMarketplace?.taxes ?? [],
                roomIds: existingMarketplace?.roomIds ?? [],
                roomUnitIds: existingMarketplace?.roomUnitIds ?? [],
                vehicleIds: existingMarketplace?.vehicleIds ?? []
            }
        ];
    };

    const marketplaceElements = useMemo<Array<Element<number>>>(
        () =>
            groomingMarketplaces
                .filter(marketplace => marketplace.active)
                .map(marketplace => ({
                    id: marketplace.id,
                    name: marketplace.name
                }))
                .sort((left, right) => left.name.toLocaleLowerCase().localeCompare(right.name.toLocaleLowerCase())),
        [groomingMarketplaces]
    );

    const selectedMarketplaceElements = useMemo<Array<Element<number>>>(
        () =>
            props.service.marketplaces
                .map(marketplace => {
                    const organizationMarketplace = organizationMarketplaces.find(
                        om => om.id === marketplace.marketplaceId
                    );

                    return organizationMarketplace
                        ? { id: organizationMarketplace.id, name: organizationMarketplace.name }
                        : null;
                })
                .filter((element): element is Element<number> => element !== null),
        [props.service.marketplaces, organizationMarketplaces]
    );

    useEffect(() => {
        // Only auto-select the logged-in marketplace when creating a service.
        if (props.service.id !== undefined || props.service.marketplaces.length > 0 || !loggedInMarketplace) {
            return;
        }

        props.onChangeMarketplaces(buildLoggedInMarketplaceEntry());
    }, [loggedInMarketplace, props.onChangeMarketplaces, props.service.id, props.service.marketplaces.length]);

    const changeHandler = (selectedPetTypesOptions: Array<Selectable<PetType>>): void => {
        props.onChange(selectedPetTypesOptions);
    };

    const changeMarketplacesSelectionHandler = (elements: Array<Element<number>>): void => {
        props.onChangeMarketplaces(
            elements.map(element => {
                const existingMarketplace = props.service.marketplaces.find(
                    marketplace => marketplace.marketplaceId === element.id
                );

                return {
                    marketplaceId: element.id,
                    staffIds: existingMarketplace?.staffIds ?? [],
                    taxes: existingMarketplace?.taxes ?? [],
                    roomIds: existingMarketplace?.roomIds ?? [],
                    roomUnitIds: existingMarketplace?.roomUnitIds ?? [],
                    vehicleIds: existingMarketplace?.vehicleIds ?? []
                };
            })
        );
    };

    const normalizedState = normalizeState(props.marketplaceBasics.address.state);
    const allowedState = isStateAllowed(normalizedState);

    return (
        <Section
            position={1}
            title="Category"
            {...props}
            collapsed={props.collapsed}
            onCollapse={props.onCollapse}
        >
            <BodySection>
                <Box className={classes.container}>
                    <Box className={classes.row}>
                        <Box className={classes.petTypesContainer}>
                            <PetTypes
                                petTypes={props.petTypes}
                                onChange={changeHandler}
                            />
                        </Box>
                        <Box className={classes.rowDivider}>
                            <MobileDivider />
                        </Box>
                        <Box
                            className={clsx(classes.businessAreaContainer, {
                                [classes.businessAreasNone]: !allowedState
                            })}
                        >
                            <BusinessArea
                                businessArea={props.service.businessArea}
                                options={props.optionsBusinessAreas}
                                onChange={props.onChangeBusinessArea}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.locationsContainer}>
                        <ChipSelector
                            id="services_add_service_category_locations"
                            title="Select locations"
                            searchLabel="Search locations"
                            allLabel="All Locations"
                            elements={marketplaceElements}
                            selectedElements={selectedMarketplaceElements}
                            changeSelection={changeMarketplacesSelectionHandler}
                        />
                    </Box>
                </Box>
            </BodySection>
            <ActionSection
                id="services_add_service_button_next"
                onNext={props.onNext}
                disabled={disabledNext}
            />
        </Section>
    );
};

export default Category;
